import { useState, useCallback, useEffect } from "react";

import {
  Button,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from "@material-ui/core";

// Karsten
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import InputText from "components/_refactor/text/input";
import PeriodField, {
  getPeriodLabel,
} from "components/_refactor/fields/period-field";

import useForm from "hooks/form";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs";
import TableMapeamentoPrateleira from "./table/TableMapeamentoPrateleira";

import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";

const validateFields = (values) => {
  const { numeroNf, periodoEmissao, pedido } = values;

  return (
    !MaskAndValid.isNullOrEmpty(numeroNf) ||
    !MaskAndValid.isNullOrEmpty(periodoEmissao) ||
    !MaskAndValid.isNullOrEmpty(pedido)
  );
};

const dataHoje = new Date().toISOString().slice(0, 10);

const situacaoOptions = [
  {
    value: "SEMENTRADA",
    name: "Entrada Não Registrada",
  },
  {
    value: "COMENTRADA",
    name: "Entrada Registrada",
  },
  {
    value: "todos",
    name: "Todos",
  },
];

const ordenacaoOptions = [
  {
    value: 1,
    name: "Data da venda (ascendente)",
  },
  {
    value: 2,
    name: "Data da venda (descendente)",
  },
  {
    value: 3,
    name: "Nome da loja (ascendente)",
  },
  {
    value: 4,
    name: "Nome da loja (descendente)",
  },
];

function MapeamentoTriangularPrateleira() {

  const [searching, setSearching] = useState(false);
  const [pedidos, setPedidos] = useState(null);
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [tableTitle, setTableTitle] = useState(null);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { values, handleChange, errors } = useForm({
    numeroNf: "",
    pedido: "",
    periodoEmissao: null,
    dataInicialEmissao: dataHoje,
    dataFinalEmissao: dataHoje,
    situacao: "SEMENTRADA",
    ordenacao: 1,
  });
  const snackbar = useSnackbar();

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    handleChange({
      target: {
        name: "numeroNf",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "pedido",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "periodoEmissao",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataInicialEmissao",
        value: dataHoje,
      },
    });
    handleChange({
      target: {
        name: "dataFinalEmissao",
        value: dataHoje,
      },
    });
    handleChange({
      target: {
        name: "situacao",
        value: "SEMENTRADA",
      },
    });
    handleChange({
      target: {
        name: "ordenacao",
        value: 1,
      },
    });

    setSearching(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setPedidos(null);
  };

  const searchOrders = useCallback(async () => {
    setSearching(true);
    setCancelLoading(false);
    setErrorMessage(false);

    await Api.get(
      "order-map-infinite-shelf",
      "reportApp",
      UtilsFncs.RestParamsComposer(
        structureFilters,
        values.numeroNf ? "?numeroNf=" + values.numeroNf : "",
        true
      ) +
        (values.periodoEmissao
          ? `&dtIni=${values.dataInicialEmissao}&dtFim=${values.dataFinalEmissao}`
          : "") +
        "&situacao=" +
        (values.situacao === "todos" ? "" : values.situacao) +
        "&ordenacao=" +
        values.ordenacao +
        "&xmlImportado=true" +
        "&idPedido=" +
        values.pedido,
      true
    )
      .then((res) => {
        if (values.periodoEmissao) {
          setTableTitle(
            `Mapeamento de Pedidos ${getPeriodLabel({
              period: values.periodoEmissao?.value,
              initialDate: values.dataInicialEmissao,
              finalDate: values.dataFinalEmissao,
            })}`
          );
        } else {
          setTableTitle("Mapeamento de Pedidos");
        }

        setPedidos(res.Result.ListaPedidos);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage(true);      })
      .finally(() => {
        setSearching(false);
      });
  }, [
    structureFilters,
    values.dataFinalEmissao,
    values.dataInicialEmissao,
    values.numeroNf,
    values.ordenacao,
    values.periodoEmissao,
    values.situacao,
    values.pedido,
  ]);

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar dados. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (
      (structureFilters?.idMarca === null &&
        structureFilters?.idCanal === "" &&
        structureFilters?.idRepresentante === "" &&
        structureFilters?.idGrupoEconomico === "") ||
      (values.numeroNf === "" && values.numeroNf === "")
    ) {
      setClearFilters(false);
    }
  }, [structureFilters, searching, values]);

  return (
    <>
      <HeaderClientes
        section="Mapeamento Triangular Prateleira Infinita"
        subsection="Pedidos"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    fieldsOrder={["economicGroup", "store"]}
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    requiredFields={["brand"]}
                    hideFields={[
                      "channel",
                      "supervisor",
                      "prepository",
                      "manager",
                    ]}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Situação"
                      required
                      options={situacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        situacaoOptions?.find(
                          (item) => item.value === values.situacao
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "situacao",
                            value: newValue ? newValue.value : "",
                          },
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <InputText
                      type="number"
                      label="NF Loja"
                      value={values.numeroNf}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "numeroNf",
                            value: e.target.value,
                          },
                        });
                      }}
                      disabled={searching}
                    />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <InputText
                      label="Pedido de Prateleira"
                      value={values.pedido}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "pedido",
                            value: e.target.value,
                          },
                        });
                      }}
                      disabled={searching}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={values.periodoEmissao?.value === "Outros" ? "12" : "3"}
                  >
                    <PeriodField
                      label="Data de Emissão"
                      value={values.periodoEmissao}
                      onChange={(option) =>
                        handleChange({
                          target: {
                            name: "periodoEmissao",
                            value: option,
                          },
                        })
                      }
                      initialDate={values.dataInicialEmissao}
                      finalDate={values.dataFinalEmissao}
                      setInitialDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataInicialEmissao",
                            value,
                          },
                        })
                      }
                      setFinalDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataFinalEmissao",
                            value,
                          },
                        })
                      }
                      disabled={searching}
                      required={true}
                    />
                  </Grid>
                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Ordenar por"
                      options={ordenacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        ordenacaoOptions?.find(
                          (item) => item.value === values.ordenacao
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "ordenacao",
                            value: newValue ? newValue.value : "",
                          },
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      {!validateFields(values) && (
                        <Typography
                          variant="caption"
                          style={{ marginBottom: "1rem" }}
                        >
                          É necessário informar Número da NF ou informar a Data
                          de Emissão da NF
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={searchOrders}
                      type="submit"
                      disabled={
                        searching ||
                        Object.keys(errors).length > 0 ||
                        structureLoading.length > 0 ||
                        (!values.numeroNf &&
                          (MaskAndValid.isNullOrEmpty(
                            structureFilters.idMarca
                          ) ||
                            MaskAndValid.isNullOrEmpty(values.situacao) ||
                            !validateFields(values) ||
                            (values.periodoEmissao &&
                              !MaskAndValid.validateDate(
                                values.dataInicialEmissao
                              ) &&
                              !MaskAndValid.validateDate(
                                values.dataFinalEmissao
                              ) &&
                              values.dataInicialEmissao <=
                                values.dataFinalEmissao)))
                      }
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              label="Buscando dados..."
            />
          </Grid>
        </Grid>
        {!searching && pedidos && !cancelLoading ? (
          <TableMapeamentoPrateleira
            dataTable={pedidos}
            searchOrders={searchOrders}
            tableTitle={tableTitle}
          />
        ) : null}
      </Container>
    </>
  );
}

export default MapeamentoTriangularPrateleira;
